/**
 * UpgradeModal Component
 *
 * Platform-aware upgrade prompt:
 * - Native iOS: RevenueCat / Apple IAP + Restore Purchases
 * - Web / PWA / Android: Stripe Checkout redirect
 */

'use client'

import { useState, useEffect, useCallback } from 'react'
import { logHydrationFirstRender, useClientMounted } from '@/lib/hydration/useClientMounted'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { PACK_LEADER_BENEFITS, PACK_LEADER_MONTHLY_PRICE } from '@/lib/monetization/packLeaderPackage'
import { WagamourV2Modal, WagamourV2Button, WagamourV2Surface } from '@/components/v2'
import { homeV2Semantic, homeV2Text, homeV2Type } from '@/lib/home/homeV2Theme'
import { appBetaProfileUrl } from '@/lib/profile/appBetaProfileUrl'
import { isCapacitorIosApp } from '@/lib/auth/capacitorSignIn'
import {
  detectNativeIosForUpgrade,
  getUpgradePlatformDebugInfo,
} from '@/lib/monetization/detectUpgradePlatform'
import {
  getNativeOAuthBlockDebugSnapshot,
  shouldBlockUpgradeDuringNativeOAuth,
  UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE,
} from '@/lib/monetization/blockUpgradeDuringNativeOAuth'
import {
  logUpgradeIapBlocked,
  logUpgradeIapTrace,
  snapshotUpgradeTapContext,
} from '@/lib/monetization/upgradeIapTrace'
import { logUpgradeFlow } from '@/lib/monetization/upgradeFlowDebugLog'
import {
  restorePurchasesFlow,
  startUpgradeFlow,
} from '@/lib/monetization/startUpgradeFlow'
import {
  fetchPackLeaderPackagePrice,
  ensureRevenueCatReadyForPurchase,
  getOfferingsLoadedState,
  getRevenueCatInitState,
  initializeRevenueCat,
  preloadPackLeaderOfferings,
  purchasePackLeaderViaRevenueCat,
  hasPackLeaderEntitlement,
  tryApplyPackLeaderTierFromRevenueCat,
} from '@/lib/monetization/revenueCat'
import {
  applyPackLeaderTierLocally,
  syncSubscriptionWithServerBestEffort,
} from '@/lib/monetization/refreshSubscriptionState'
import {
  isSubscriptionVerificationUserMessage,
  sanitizePaywallUserMessage,
  SUBSCRIPTION_PURCHASE_COMPLETED_REFRESHING_MESSAGE,
  SUBSCRIPTION_PURCHASE_UNAVAILABLE_USER_MESSAGE,
  SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE,
} from '@/lib/monetization/iapConstants'
import { trackUpgradeCompleted } from '@/lib/subscription/analytics'
import {
  forceClearNativeInteractionLocks,
  logPostCancelTapState,
} from '@/lib/monetization/forceClearNativeInteractionLocks'

export interface UpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  feature: string
  valueProposition?: string
}

interface UpgradeProfileState {
  userId: string | null
  email?: string
  name?: string
  needsSetup: boolean
}

async function resolveUpgradeProfile(): Promise<UpgradeProfileState> {
  let userId: string | null = null
  let email: string | undefined
  let name: string | undefined
  let needsSetup = false

  try {
    const meRes = await fetch('/api/auth/me', { credentials: 'include' })
    if (meRes.ok) {
      const meData = await meRes.json()
      if (meData?.user?.id) {
        userId = meData.user.id
        email = meData.user.email ?? undefined
        name = meData.user.name ?? undefined
      }
    }
  } catch {
    /* fall through to cache */
  }

  if (!userId) {
    try {
      const savedProfile = localStorage.getItem('wagamour_user_profile')
      if (savedProfile) {
        const parsed = JSON.parse(savedProfile)
        if (parsed?.userId) {
          userId = parsed.userId
          email = parsed.email ?? email
          name = parsed.name ?? parsed.firstName ?? name
        }
      }
    } catch {
      /* ignore */
    }
  }

  if (!userId) {
    return { userId: null, email, name, needsSetup: true }
  }

  try {
    const setupRes = await fetch('/api/profile/setup', { credentials: 'include' })
    if (setupRes.ok) {
      const setupData = await setupRes.json()
      needsSetup = !!setupData.needsSetup
    } else if (setupRes.status === 401) {
      needsSetup = true
    }
  } catch {
    /* allow upgrade attempt if setup check fails and we have userId */
  }

  return { userId, email, name, needsSetup }
}

function resolveNativeIosUpgradeModal(): boolean {
  return detectNativeIosForUpgrade() || isCapacitorIosApp()
}

export function UpgradeModal({
  isOpen,
  onClose,
  feature,
  valueProposition,
}: UpgradeModalProps) {
  const hasClientMounted = useClientMounted('UpgradeModal')
  const router = useRouter()
  const { data: session, update: updateSession, status: sessionStatus } = useSession()
  const sessionUserId = (session?.user as { id?: string } | undefined)?.id
  const [profileState, setProfileState] = useState<UpgradeProfileState>({
    userId: null,
    needsSetup: false,
  })
  const [profileResolving, setProfileResolving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [priceIdConfigured, setPriceIdConfigured] = useState<boolean>(true)
  const [showProfileCTA, setShowProfileCTA] = useState(false)
  const [iosPriceLabel, setIosPriceLabel] = useState<string | null>(null)
  const [isIosNative, setIsIosNative] = useState(false)
  const [presentation, setPresentation] = useState<'center' | 'sheet'>('center')
  const [offeringsLoaded, setOfferingsLoaded] = useState(false)

  const profileNotReady =
    !profileResolving && (!profileState.userId || profileState.needsSetup)
  const profileBlocked = showProfileCTA || profileNotReady
  const iosPurchaseInFlight = isLoading || isRestoring
  const webPurchaseBlocked =
    iosPurchaseInFlight ||
    profileResolving ||
    (!priceIdConfigured && !isIosNative) ||
    profileBlocked
  const buttonDisabled = isIosNative ? iosPurchaseInFlight : webPurchaseBlocked

  const handleClose = useCallback(() => {
    setIsLoading(false)
    setIsRestoring(false)
    // Close first so scroll unlock runs; clear Cap locks after paint.
    onClose()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        forceClearNativeInteractionLocks('upgrade_modal_close')
      })
    })
  }, [onClose])

  useEffect(() => {
    if (!hasClientMounted) return
    setIsIosNative(resolveNativeIosUpgradeModal())
  }, [hasClientMounted, isOpen])

  useEffect(() => {
    if (!hasClientMounted) return
    const mq = window.matchMedia('(max-width: 639px)')
    const update = () => setPresentation(mq.matches ? 'sheet' : 'center')
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [hasClientMounted])

  useEffect(() => {
    logHydrationFirstRender('UpgradeModal', {
      isOpen,
      feature,
      isLoading,
      error,
      hasClientMounted: hasClientMounted,
      isIosNative,
    })
  }, [isOpen, feature, isLoading, error, hasClientMounted, isIosNative])

  useEffect(() => {
    if (!isOpen) return
    logUpgradeFlow('modal opened', getUpgradePlatformDebugInfo())
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    setError(null)
    setSuccessMessage(null)
    setShowProfileCTA(false)
    setPriceIdConfigured(true)
    setOfferingsLoaded(false)

    if (isIosNative) {
      fetchPackLeaderPackagePrice()
        .then((price) => {
          if (price?.priceString) setIosPriceLabel(price.priceString)
        })
        .catch(() => {
          /* fallback to static copy */
        })
    } else {
      setIosPriceLabel(null)
    }
  }, [isOpen, isIosNative])

  useEffect(() => {
    if (!isOpen || !isIosNative || sessionStatus === 'loading') return

    let cancelled = false
    ;(async () => {
      try {
        const resolved = await resolveUpgradeProfile()
        if (cancelled || !resolved.userId || resolved.needsSetup) return
        try {
          await initializeRevenueCat(resolved.userId)
          const loaded = await preloadPackLeaderOfferings('modal_background_prep')
          if (!cancelled) {
            setOfferingsLoaded(loaded || getOfferingsLoadedState())
            if (loaded) {
              const price = await fetchPackLeaderPackagePrice()
              if (price?.priceString) setIosPriceLabel(price.priceString)
            }
          }
        } catch (initError) {
          console.warn('[UpgradeModal] background RevenueCat init failed:', initError)
        }
      } catch {
        /* non-fatal */
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isOpen, isIosNative, sessionStatus])

  useEffect(() => {
    if (!isOpen || sessionStatus === 'loading') return

    let cancelled = false
    setProfileResolving(true)

    resolveUpgradeProfile()
      .then((resolved) => {
        if (cancelled) return
        setProfileState(resolved)
      })
      .catch((loadError) => {
        console.error('[UpgradeModal] Error resolving profile:', loadError)
        if (!cancelled) {
          setProfileState({ userId: null, needsSetup: true })
        }
      })
      .finally(() => {
        if (!cancelled) setProfileResolving(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, sessionStatus])

  const handleGoToProfile = () => {
    handleClose()
    router.push(appBetaProfileUrl('upgrade'))
  }

  const handleUpgrade = useCallback(async () => {
    logUpgradeIapTrace('handleUpgrade enter', {
      feature,
      ...snapshotUpgradeTapContext(sessionStatus, sessionUserId),
    })
    console.log('[UpgradeTrace9419bd7] HANDLE_UPGRADE_ENTER')
    console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.enter', {
      feature,
      pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    })

    if (shouldBlockUpgradeDuringNativeOAuth()) {
      logUpgradeIapBlocked('native_oauth_in_progress', getNativeOAuthBlockDebugSnapshot())
      console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
        path: 'blocked_native_oauth',
      })
      setError(UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE)
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccessMessage(null)
    setShowProfileCTA(false)

    const isNativeIosUpgrade = resolveNativeIosUpgradeModal()
    logUpgradeIapTrace('native routing decision', {
      isNativeIosUpgrade,
      isCapacitorIosApp: isCapacitorIosApp(),
      detectNativeIosForUpgrade: detectNativeIosForUpgrade(),
      sessionStatus,
      userId: sessionUserId ? `${sessionUserId.slice(0, 8)}…` : null,
      feature,
    })
    console.log(
      '[UpgradeTrace9419bd7] native_click ' +
        JSON.stringify({
          isNativeIosUpgrade,
          isCapacitorIosApp: isCapacitorIosApp(),
          detectNativeIosForUpgrade: detectNativeIosForUpgrade(),
          feature,
        })
    )

    if (isNativeIosUpgrade) {
      try {
        const resolved = await resolveUpgradeProfile()
        setProfileState(resolved)
        console.log(
          '[UpgradeTrace9419bd7] native_resolved_profile ' +
            JSON.stringify({
              hasUserId: Boolean(resolved.userId),
              needsSetup: resolved.needsSetup,
            })
        )

        if (!resolved.userId) {
          logUpgradeIapBlocked('profile_not_ready', {
            hasUserId: false,
            needsSetup: resolved.needsSetup,
            sessionStatus,
          })
          console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
            path: 'profile_not_ready',
          })
          setShowProfileCTA(true)
          setIsLoading(false)
          return
        }

        logUpgradeIapTrace('before initializeRevenueCat', {
          userId: resolved.userId.slice(0, 8),
          sessionStatus,
        })
        console.log('[UpgradeTrace9419bd7] native_before_initializeRevenueCat')

        const purchaseReady = await ensureRevenueCatReadyForPurchase(resolved.userId)
        setOfferingsLoaded(purchaseReady || getOfferingsLoadedState())
        console.log('[UpgradeTrace9419bd7] native_after_initializeRevenueCat')

        const rcState = getRevenueCatInitState()
        console.log(
          '[UpgradeTrace9419bd7] native_rc_state ' +
            JSON.stringify({ state: rcState.state, error: rcState.error })
        )
        logUpgradeFlow('RevenueCat offering/package', {
          initState: rcState.state,
          initError: rcState.error,
          offeringsLoaded: getOfferingsLoadedState(),
        })

        if (!purchaseReady) {
          logUpgradeIapBlocked('revenuecat_not_ready_for_purchase', {
            initState: rcState.state,
            initError: rcState.error,
            sessionStatus,
          })
          console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
            path: 'revenuecat_not_ready_for_purchase',
            initState: rcState.state,
          })
          setError(SUBSCRIPTION_PURCHASE_UNAVAILABLE_USER_MESSAGE)
          setIsLoading(false)
          return
        }

        logUpgradeIapTrace('before purchasePackLeaderViaRevenueCat', {
          userId: resolved.userId.slice(0, 8),
          sessionStatus,
          feature,
        })
        console.log(
          '[UpgradeTrace9419bd7] UpgradeModal.native_iap_start ' +
            JSON.stringify({ feature, userId: resolved.userId.slice(0, 8) })
        )
        console.log('[UpgradeTrace9419bd7] native_before_purchase')

        const purchase = await purchasePackLeaderViaRevenueCat()

        console.log('[UpgradeTrace9419bd7] native_after_purchase')
        console.log(
          '[UpgradeTrace9419bd7] UpgradeModal.native_iap_result ' +
            JSON.stringify({
              ok: purchase.ok,
              cancelled: purchase.ok ? undefined : purchase.cancelled,
              message: purchase.ok ? undefined : purchase.message,
            })
        )

        if (!purchase.ok) {
          logUpgradeIapTrace('purchasePackLeaderViaRevenueCat failed', {
            cancelled: purchase.cancelled,
            message: purchase.message,
            sessionStatus,
          })
          if (purchase.cancelled) {
            handleClose()
            window.requestAnimationFrame(() => {
              forceClearNativeInteractionLocks('upgrade_modal_purchase_cancelled')
              logPostCancelTapState('upgrade_modal_purchase_cancelled')
            })
            return
          }
          if (
            purchase.message === SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE ||
            purchase.message === SUBSCRIPTION_PURCHASE_COMPLETED_REFRESHING_MESSAGE
          ) {
            const recovery = await tryApplyPackLeaderTierFromRevenueCat(
              'upgrade_modal_purchase_recovery'
            )
            if (recovery.applied) {
              trackUpgradeCompleted(resolved.userId, feature)
              setError(null)
              setSuccessMessage(
                purchase.message === SUBSCRIPTION_PURCHASE_COMPLETED_REFRESHING_MESSAGE
                  ? SUBSCRIPTION_PURCHASE_COMPLETED_REFRESHING_MESSAGE
                  : 'Pack Leader is already active on this account.'
              )
              setIsLoading(false)
              void syncSubscriptionWithServerBestEffort().then(async (sync) => {
                try {
                  await updateSession()
                } catch {
                  /* non-fatal */
                }
                router.refresh()
              })
              setTimeout(() => handleClose(), 1200)
              return
            }
          }
          forceClearNativeInteractionLocks('upgrade_modal_purchase_failed')
          setError(sanitizePaywallUserMessage(purchase.message, 'purchase_failed'))
          setIsLoading(false)
          return
        }

        logUpgradeIapTrace('post-purchase client entitlement confirmed', {
          hasPackLeader: hasPackLeaderEntitlement(purchase.customerInfo),
          alreadySubscribed: purchase.alreadySubscribed ?? false,
          feature,
          sessionStatus,
        })

        applyPackLeaderTierLocally('upgrade_modal_purchase_success')
        trackUpgradeCompleted(resolved.userId, feature)

        setError(null)
        setSuccessMessage(
          purchase.alreadySubscribed
            ? 'Pack Leader is already active on this account.'
            : 'Welcome to Pack Leader! Your premium features are now unlocked.'
        )
        setIsLoading(false)

        void syncSubscriptionWithServerBestEffort().then(async () => {
          try {
            await updateSession()
          } catch {
            /* non-fatal */
          }
          router.refresh()
        })

        setTimeout(() => handleClose(), 1200)
        console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
          path: 'revenuecat_success_client_entitlement',
        })
        return
      } catch (nativeErr) {
        const err = nativeErr instanceof Error ? nativeErr : new Error(String(nativeErr))
        logUpgradeIapTrace('native upgrade exception', {
          message: err.message,
          sessionStatus,
        })
        console.error(
          '[UpgradeTrace9419bd7] native_upgrade_exception ' +
            JSON.stringify({
              message: err.message,
              name: err.name,
              stack: err.stack,
              raw: nativeErr,
            })
        )
        forceClearNativeInteractionLocks('upgrade_modal_native_exception')
        logPostCancelTapState('upgrade_modal_native_exception')
        setIsLoading(false)
        setError(sanitizePaywallUserMessage(err.message, 'purchase_failed'))
        return
      } finally {
        setIsLoading(false)
        setIsRestoring(false)
      }
    }

    logUpgradeIapBlocked('stripe_web_path', {
      isNativeIosUpgrade,
      sessionStatus,
      reason: 'not_native_ios_upgrade_modal',
    })

    try {
      const resolved = await resolveUpgradeProfile()
      setProfileState(resolved)

      if (!resolved.userId || resolved.needsSetup) {
        logUpgradeIapBlocked('profile_not_ready_stripe_path', {
          hasUserId: Boolean(resolved.userId),
          needsSetup: resolved.needsSetup,
        })
        console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
          path: 'profile_not_ready',
        })
        setShowProfileCTA(true)
        setIsLoading(false)
        return
      }

      const result = await startUpgradeFlow({
        userId: resolved.userId,
        feature,
        email: resolved.email,
        name: resolved.name,
        isNativeIosUpgrade: false,
      })

      console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.result', {
        path: 'stripe_web',
        success: result.success,
        provider: result.success ? result.provider : undefined,
        error: result.success ? undefined : result.error,
        cancelled: !result.success ? result.cancelled : undefined,
      })

      if (!result.success) {
        if (result.cancelled) {
          console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
            path: 'stripe_web_cancelled',
          })
          handleClose()
          window.requestAnimationFrame(() => {
            forceClearNativeInteractionLocks('upgrade_modal_stripe_cancelled')
            logPostCancelTapState('upgrade_modal_stripe_cancelled')
          })
          return
        }

        forceClearNativeInteractionLocks('upgrade_modal_stripe_failed')
        if (
          result.error.includes('Payment configuration') ||
          result.error.includes('not configured')
        ) {
          setPriceIdConfigured(false)
          setError('Payment configuration error. Please try again later.')
        } else {
          setError(result.error || 'Unable to start checkout. Please try again in a moment.')
        }
        console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
          path: 'stripe_web_failed',
          error: result.error,
        })
        setIsLoading(false)
        return
      }

      if (result.provider === 'revenuecat') {
        setSuccessMessage('Welcome to Pack Leader! Your premium features are now unlocked.')
        setIsLoading(false)
        try {
          await updateSession()
        } catch {
          /* non-fatal */
        }
        router.refresh()
        setTimeout(() => handleClose(), 1200)
        console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
          path: 'stripe_web_revenuecat_unexpected',
        })
        return
      }

      console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
        path: 'stripe_redirect_in_progress',
      })
      // Stripe: redirect in progress (window.location set in startStripeCheckout)
    } catch (upgradeError) {
      console.error('[UpgradeModal] Upgrade error:', upgradeError)
      console.log('[UpgradeTrace9419bd7] UpgradeModal.handleUpgrade.exit', {
        path: 'exception',
      })
      forceClearNativeInteractionLocks('upgrade_modal_exception')
      setIsLoading(false)
      setError('Unable to start checkout. Please try again in a moment.')
    }
  }, [feature, updateSession, router, handleClose, sessionStatus, sessionUserId])

  const handleUpgradeButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      logUpgradeIapTrace('tap Pack Leader', {
        buttonDisabled,
        isLoading,
        isRestoring,
        profileResolving,
        profileBlocked,
        priceIdConfigured,
        isIosNative,
        hasClientMounted,
        feature,
        ...snapshotUpgradeTapContext(sessionStatus, sessionUserId),
      })
      console.log(
        '[UpgradeTrace9419bd7] BUTTON_CLICKED ' +
          JSON.stringify({
            buttonDisabled,
            isLoading,
            isRestoring,
            profileResolving,
            profileBlocked,
            priceIdConfigured,
            isIosNative,
            hasClientMounted: hasClientMounted,
            feature,
          })
      )
      if (buttonDisabled) {
        logUpgradeIapBlocked('upgrade_button_disabled', {
          isLoading,
          isRestoring,
          profileResolving,
          profileBlocked,
          priceIdConfigured,
          isIosNative,
        })
        return
      }
      void handleUpgrade()
    },
    [
      buttonDisabled,
      feature,
      handleUpgrade,
      hasClientMounted,
      isIosNative,
      isLoading,
      isRestoring,
      priceIdConfigured,
      profileBlocked,
      profileResolving,
      sessionStatus,
      sessionUserId,
    ]
  )

  const handleRestorePurchases = async () => {
    if (shouldBlockUpgradeDuringNativeOAuth()) {
      setError(UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE)
      return
    }

    setIsRestoring(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const result = await restorePurchasesFlow()
      if (!result.success) {
        setError(
          sanitizePaywallUserMessage(
            result.error,
            isSubscriptionVerificationUserMessage(result.error)
              ? 'restore_verify'
              : 'restore_failed'
          )
        )
        setIsRestoring(false)
        return
      }

      if (result.restored) {
        setSuccessMessage('Purchases restored. Pack Leader is active on this account.')
        try {
          await updateSession()
        } catch {
          /* non-fatal */
        }
        router.refresh()
        setTimeout(() => handleClose(), 1200)
      } else {
        setError('No active Pack Leader subscription was found for this Apple ID.')
      }
    } catch (restoreError) {
      console.error('[UpgradeModal] Restore error:', restoreError)
      setError(sanitizePaywallUserMessage(null, 'restore_failed'))
    } finally {
      setIsRestoring(false)
      forceClearNativeInteractionLocks('upgrade_modal_restore_finally')
    }
  }

  const getValueProposition = (): string => {
    if (valueProposition) return valueProposition

    switch (feature) {
      case 'park_safety_intelligence':
        return 'Is this park safe right now for your dog? Get real-time heat, shade, crowd & dog-specific safety insights.'
      case 'why_this_park':
      case 'why_this_score':
        return 'Understand exactly why this park is perfect for your dog, with personalized explanations based on their needs.'
      case 'smart_walk_intelligence':
        return 'Get real-time heat, shade, crowd & dog-specific safety insights for the perfect walk time.'
      case 'ai_picks':
        return "Get personalized product recommendations tailored to your dog's unique needs and preferences."
      case 'photo_upload':
        return 'Share park photos to help the community and get better AI insights for safer dog outings.'
      case 'community_analysis':
        return 'See AI-summarized Community Intelligence™ trends to keep your dog safe and happy.'
      default:
        return 'Unlock deeper insights and personalized recommendations to keep your dog safe and happy.'
    }
  }

  const priceDisplay =
    isIosNative && iosPriceLabel
      ? iosPriceLabel
      : PACK_LEADER_MONTHLY_PRICE.replace('/month', '')
  const billingNote =
    hasClientMounted && isIosNative
      ? 'Subscription managed through your Apple ID. Cancel anytime in Settings.'
      : 'Cancel anytime'

  const profileNotReadyForRender =
    !profileResolving && (!profileState.userId || profileState.needsSetup)
  const profileBlockedForRender = showProfileCTA || profileNotReadyForRender

  const upgradeButtonLabel =
    isLoading || profileResolving
      ? 'Loading...'
      : hasClientMounted && !priceIdConfigured && !isIosNative
        ? 'Payment Unavailable'
        : 'Upgrade to Pack Leader'

  return (
    <WagamourV2Modal
      isOpen={isOpen}
      onClose={handleClose}
      presentation={presentation}
      header={
        <div className="pr-2">
          <h2 className="text-2xl font-bold" style={homeV2Type(homeV2Text.headline)}>
            Pack Leader Monthly
          </h2>
          <p className="mt-1 text-sm font-medium" style={homeV2Type(homeV2Text.section)}>
            Auto-renewable monthly subscription
          </p>
        </div>
      }
      footer={
        <div className="space-y-3">
          {profileBlockedForRender && (
            <WagamourV2Surface variant="utility" className="space-y-3 p-3">
              <p className="text-sm" style={homeV2Type(homeV2Text.body)}>
                {profileState.userId
                  ? 'Add your name to complete your profile before upgrading.'
                  : 'Please sign in and complete your profile to continue.'}
              </p>
              <WagamourV2Button variant="primary" fullWidth onClick={handleGoToProfile}>
                Go to Profile
              </WagamourV2Button>
            </WagamourV2Surface>
          )}

          {successMessage && (
            <WagamourV2Surface variant="utility" className="p-3">
              <p className="text-sm" style={{ color: homeV2Semantic.heat.low.text }}>
                {successMessage}
              </p>
            </WagamourV2Surface>
          )}

          {error && (
            <WagamourV2Surface variant="utility" className="p-3">
              <p className="text-sm" style={{ color: homeV2Semantic.heat.high.text }}>
                {error}
              </p>
            </WagamourV2Surface>
          )}

          {!priceIdConfigured && hasClientMounted && !isIosNative && (
            <WagamourV2Surface variant="utility" className="p-3">
              <p className="text-sm" style={{ color: homeV2Semantic.heat.moderate.text }}>
                Payment configuration error. Please try again later.
              </p>
            </WagamourV2Surface>
          )}

          <div className="flex gap-3">
            <WagamourV2Button variant="secondary" fullWidth onClick={handleClose}>
              Maybe Later
            </WagamourV2Button>
            <WagamourV2Button
              variant="primary"
              fullWidth
              disabled={buttonDisabled}
              onClick={handleUpgradeButtonClick}
            >
              {upgradeButtonLabel}
            </WagamourV2Button>
          </div>

          {hasClientMounted && isIosNative && (
            <WagamourV2Button
              variant="secondary"
              fullWidth
              size="sm"
              disabled={isLoading || isRestoring}
              onClick={handleRestorePurchases}
              className="!min-h-[44px] !border-white/15 !bg-white/5 !shadow-none underline underline-offset-2"
            >
              {isRestoring ? 'Restoring purchases…' : 'Restore Purchases'}
            </WagamourV2Button>
          )}

          <p className="text-center text-xs" style={homeV2Type(homeV2Text.muted)}>
            Park discovery and community contribution remain free for everyone.
          </p>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-base font-semibold" style={homeV2Type(homeV2Text.primary)}>
            Unlock Pet Comfort Intelligence™
          </p>
          <p className="mt-1 text-sm leading-relaxed" style={homeV2Type(homeV2Text.body)}>
            {getValueProposition()}
          </p>
        </div>

        <WagamourV2Surface variant="utility" className="p-4">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold" style={homeV2Type(homeV2Text.headline)}>
              {priceDisplay}
            </span>
            <span className="text-sm" style={homeV2Type(homeV2Text.secondary)}>
              /month
            </span>
          </div>
          <p className="mt-1 text-sm" style={homeV2Type(homeV2Text.metadata)}>
            {billingNote}
          </p>
        </WagamourV2Surface>

        {hasClientMounted && isIosNative && (
          <WagamourV2Surface variant="utility" className="space-y-2 p-3">
            <p className="text-xs leading-relaxed" style={homeV2Type(homeV2Text.metadata)}>
              Payment will be charged to your Apple ID at confirmation of purchase.
              Subscription automatically renews unless canceled at least 24 hours before
              the end of the current period.
            </p>
            <p className="text-xs" style={homeV2Type(homeV2Text.metadata)}>
              <a
                href="https://wagamour.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline"
                style={{ color: homeV2Text.link }}
              >
                Privacy Policy
              </a>
              <span className="mx-1 opacity-60">·</span>
              <a
                href="https://wagamour.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline"
                style={{ color: homeV2Text.link }}
              >
                Terms of Use
              </a>
            </p>
          </WagamourV2Surface>
        )}

        <div className="space-y-2">
          {PACK_LEADER_BENEFITS.map((benefit) => (
            <div key={benefit} className="flex items-start gap-3">
              <div
                className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
                style={{
                  backgroundColor: 'rgba(251, 146, 60, 0.22)',
                  border: '1px solid rgba(255, 220, 170, 0.35)',
                }}
              >
                <span className="text-xs font-semibold" style={{ color: homeV2Text.warm }}>
                  ✓
                </span>
              </div>
              <p className="text-sm leading-relaxed" style={homeV2Type(homeV2Text.body)}>
                {benefit}
              </p>
            </div>
          ))}
        </div>
      </div>
    </WagamourV2Modal>
  )
}
